'use client';
import type {
  IMessage,
  IMessagesQueryParams,
} from '@ui/components/organisms/message/message.typings';
import type { ContextUserType } from '@ui/constants/context';
import { useMessageContext } from '@ui/components/organisms/message/message.context';
import { COMMON_REACTIONS } from '@ui/components/organisms/message/message.constants';
import { Button } from '@ui/components/atoms/button/button';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';
import { useInteractionGuard } from '@hooks/hooks/useInteractionGuard';
import { SmilePlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { sendGAEvent } from '@next/third-parties/google';

interface IProps {
  message: IMessage;
  contextUserType: ContextUserType;
  query: IMessagesQueryParams;
  triggerScoreAnimation: () => void;
}

export const PostCommentReactionPanel = ({
  message,
  contextUserType,
  query,
  triggerScoreAnimation,
}: IProps) => {
  const [openEmoji, setOpenEmoji] = useState(false);
  const messageState = useMessageContext(contextUserType);
  const { interceptInteraction } = useInteractionGuard();

  const joinMessage = useMemo(() => {
    const creatorDisplayName =
      (messageState as any)?.community_details?.creator_display_name ||
      (message as any)?.community_details?.creator_display_name ||
      (message as any)?.creator_display_name;
    return creatorDisplayName
      ? `Join community to use this feature. Collect this badge to join community!`
      : 'Join this KOR Space to use this feature. Collect this badge to join.';
  }, [messageState, message]);

  const onReactToMessage = interceptInteraction((event: any, emoji: string) => {
    event.stopPropagation();
    const userReactions = message?.user_reactions?.[messageState.user_id] || [];
    const isRemoving = userReactions.includes(emoji);
    messageState.onReactToMessage(message.id, message.slug, 'reaction', emoji, query);
    // Send GA events
    sendGAEvent('event', 'UserReaction', {
      event: 'emoji_reaction',
      event_action: isRemoving ? 'Remove Emoji' : 'Add Emoji',
      event_label: emoji,
      post_id: message.id,
      user_type: contextUserType,
    });
    !isRemoving && triggerScoreAnimation();
    setOpenEmoji(false);
  }, joinMessage);

  const onTriggerClick = interceptInteraction((e: any) => {
    e.stopPropagation();
    setOpenEmoji(true);
  }, joinMessage);

  const showAddEmoji = useMemo(() => {
    if (message?.reaction?.reactions_list) {
      return Object.keys(message?.reaction?.reactions_list).length < 6;
    }
    return false;
  }, [message?.reaction?.reactions_list]);

  return (
    <div className="flex flex-row relative items-center">
      <Popover open={openEmoji} onOpenChange={setOpenEmoji}>
        <PopoverTrigger asChild>
          <Button
            type="icon-primary"
            className="h-6 w-6 rounded-sm border-none bg-transparent hover:bg-black/5"
            onClick={onTriggerClick}>
            <SmilePlus className="h-5 w-5 text-black" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="center"
          sideOffset={8}
          className="w-[97vw] rounded-full sm:w-fit p-2 shadow-2xl border bg-white !z-[9999999]">
          <div className="flex w-full items-center justify-center gap-4 sm:gap-1">
            {COMMON_REACTIONS.map((emoji) => (
              <div
                key={`emoji-${emoji}`}
                className="hover:bg-inputActive/50 h-8 w-8 cursor-pointer flex items-center justify-center rounded-sm border-none bg-transparent text-2xl sm:text-lg transition-colors"
                onClick={(e) => onReactToMessage(e, emoji)}>
                {emoji}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
